import { firebaseAuthHeaders } from './authHeaders';
import { resolvedApiBaseUrl, resolvedAuthApiBaseUrl } from './apiBase';
import { isLoopbackUrl } from './loopbackUrl';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Join API path without trailing slashes (Go routes are exact-match). */
function apiUrl(path: string): string {
  const base = resolvedApiBaseUrl();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized.replace(/\/+$/, '')}`;
}

function authApiUrl(path: string): string {
  const base = resolvedAuthApiBaseUrl();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized.replace(/\/+$/, '')}`;
}

/** Cross-origin ops auth (OTP). Omits cookies when the auth host is not the page API. */
export async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith('http') ? path.replace(/\/+$/, '') : authApiUrl(path);
  const sameOrigin = resolvedAuthApiBaseUrl() === resolvedApiBaseUrl();
  const res = await fetch(url, {
    ...init,
    credentials: sameOrigin ? 'include' : 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new ApiError(body.error ?? res.statusText, res.status, body.code);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

/** Shared fetch for Communio API — session cookies, JSON body. */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let url = path.startsWith('http') ? path.replace(/\/+$/, '') : apiUrl(path);
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && isLoopbackUrl(url)) {
    url = apiUrl(path.startsWith('http') ? new URL(path).pathname : path);
  }
  const authHeaders = await firebaseAuthHeaders();
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new ApiError(body.error ?? res.statusText, res.status, body.code);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}
